import { useEffect, useState, forwardRef, Ref } from 'react';
import { AllieInputNumberProps } from './InputNumber.types';
import {
  StyledInputNumberContainer,
  StyledInputNumberButton,
  StyledInputNumber,
} from './InputNumber.styles';
import { AllieAddCircleIcon, AllieRemoveCircleOutlineIcon } from '../../../styling/icons';

const getNormalStepValue = (
  currentValue: number,
  increment: boolean,
  step: number,
  min: number | undefined,
  max: number | undefined
): number => {
  const remainder = (currentValue - (min || 0)) % step;

  if (increment) {
    const nextValue = remainder === 0 
      ? currentValue + step 
      : currentValue + (step - remainder);
    return max !== undefined ? Math.min(nextValue, max) : nextValue;
  } else {
    const nextValue = remainder === 0 ? currentValue - step : currentValue - remainder;
    return min !== undefined ? Math.max(nextValue, min) : nextValue;
  }
};

const getMultipleStepValue = (
  currentValue: number,
  increment: boolean,
  step: number,
  min: number | undefined,
  max: number | undefined
): number => {
  if (increment) {
    if (currentValue % step === 0) {
      const nextValue = currentValue + step;
      return max !== undefined ? Math.min(nextValue, max) : nextValue;
    } else {
      const nextValue = Math.ceil(currentValue / step) * step;
      return max !== undefined ? Math.min(nextValue, max) : nextValue;
    }
  } else {
    if (currentValue % step === 0) {
      const nextValue = currentValue - step;
      return min !== undefined ? Math.max(nextValue, min) : nextValue;
    } else {
      const nextValue = Math.floor(currentValue / step) * step;
      return min !== undefined ? Math.max(nextValue, min) : nextValue;
    }
  }
};

const getNextStepValue = (
  currentValue: number | undefined,
  increment: boolean,
  multipleMode: boolean,
  step: number,
  min: number | undefined,
  max: number | undefined
): number => {
  if (currentValue === undefined) {
    return increment ? (min !== undefined ? min : 0) : min !== undefined ? min : 0;
  }

  return multipleMode 
    ? getMultipleStepValue(currentValue, increment, step, min, max) 
    : getNormalStepValue(currentValue, increment, step, min, max);
};

export const AllieInputNumber = forwardRef(
  (
    {
      value = 0,
      onChange,
      min,
      max,
      step = 1,
      multipleMode = false,
      allieType = 'primary',
      corner = 'rounded-edges-plus',
      allieSize = 'normal',
      disabled = false,
      shadow = false,
      placeholder,
      ...props
    }: AllieInputNumberProps,
    ref
  ) => {
    const [inputValue, setInputValue] = useState<number | undefined>(
      value !== undefined ? value : undefined
    );

    useEffect(() => {
      if (value !== undefined && value !== inputValue) {
        setInputValue(value);
      }
    }, [value]);

    const handleChange = (event: any) => {
      const newValue = event.target.value === '' ? undefined : Number(event.target.value);
      setInputValue(newValue);
      if (onChange && newValue !== undefined) {
        onChange(newValue);
      }
    };

    const handleIncrement = () => {
      if (disabled) return;

      const nextValue = getNextStepValue(inputValue, true, multipleMode, step, min, max);
      setInputValue(nextValue);
      if (onChange) {
        onChange(nextValue);
      }
    };

    const handleDecrement = () => {
      if (disabled) return;

      const nextValue = getNextStepValue(inputValue, false, multipleMode, step, min, max);
      setInputValue(nextValue);
      if (onChange) {
        onChange(nextValue);
      }
    };

    const handleBlur = () => {
      if (inputValue !== undefined) {
        let validatedValue = inputValue;
        if (min !== undefined && inputValue < min) {
          validatedValue = min;
        }
        if (max !== undefined && inputValue > max) {
          validatedValue = max;
        }

        if (validatedValue !== inputValue) {
          setInputValue(validatedValue);
          if (onChange) {
            onChange(validatedValue);
          }
        }
      }
    };

    return (
      <StyledInputNumberContainer className="input-number-container">
        <StyledInputNumberButton
          onClick={handleDecrement}
          disabled={
            disabled || (min !== undefined && inputValue !== undefined && inputValue <= min)
          }
          role="button"
          className="input-number-button"
        >
          <AllieRemoveCircleOutlineIcon />
        </StyledInputNumberButton>

        <StyledInputNumber
          ref={ref as Ref<HTMLIonInputElement>}
          allieType={allieType}
          corner={corner}
          allieSize={allieSize}
          disabled={disabled}
          shadow={shadow}
          value={inputValue}
          onIonChange={handleChange}
          onIonBlur={handleBlur}
          type="number"
          min={min}
          max={max}
          placeholder={placeholder}
          {...props}
        />

        <StyledInputNumberButton
          onClick={handleIncrement}
          disabled={
            disabled || (max !== undefined && inputValue !== undefined && inputValue >= max)
          }
          role="button"
          className="input-number-button"
        >
          <AllieAddCircleIcon />
        </StyledInputNumberButton>
      </StyledInputNumberContainer>
    );
  }
);
